import AbstractProvider from '../App/AbstractProvider';
import {IStoreEvents} from '../../@interfaces/services/StoreEvents/IStoreEvents';
import {
	THEME_OPEN_MOBILE_SIDEBAR,
	THEME_SET_DARK,
	THEME_SET_LIGHT,
	THEME_SET_SMALL_SIDEBAR_SIZE,
	THEME_TOGGLE,
	THEME_TOGGLE_MOBILE_SIDEBAR,
	THEME_TOGGLE_SIDEBAR_SIZE
} from '../../_redux/theme/constants';
import {ITheme} from '../../@interfaces/services/Theme/ITheme';
import AppSetting from '../Database/Models/AppSetting';

export default class ThemeProvider extends AbstractProvider {
	readonly holdBootUntilNotCompleted: boolean = false;

	async boot() {
		this.subscribeToStoreEvents();

		const [
			activeTheme,
			mobileSidebarOpen,
			desktopSidebarSize,
		] = await Promise.all([
			AppSetting.get('theme', 'active-theme'),
			AppSetting.get('theme', 'mobile-sidebar-open'),
			AppSetting.get('theme', 'desktop-sidebar-size'),
		]);

		this.service[activeTheme === 'light' ? 'setLightTheme' : 'setDarkTheme']();
		this.service[mobileSidebarOpen ? 'openMobileSidebar' : 'closeMobileSidebar']();
		this.service[desktopSidebarSize === 'small' ? 'setDesktopSidebarSmallSize' : 'setDesktopSidebarBigSize']();
	}

	protected subscribeToStoreEvents() {
		const dispatcher = this.getStoredService<IStoreEvents>('store-events');

		dispatcher.listen(THEME_SET_DARK, () => {
			this.toggleDarkTheme(true);

			return AppSetting.updateOrCreate<AppSetting>({
				service: 'theme',
				name: 'active-theme',
			}, {
				value: 'dark',
			});
		});

		dispatcher.listen(THEME_SET_LIGHT, () => {
			this.toggleDarkTheme(false);

			return AppSetting.updateOrCreate<AppSetting>({
				service: 'theme',
				name: 'active-theme',
			}, {
				value: 'light',
			});
		});

		dispatcher.listen(THEME_TOGGLE, (event, payload) => {
			const setDarkThemeAsActive = !payload.api.getState().theme.dark;
			this.toggleDarkTheme(setDarkThemeAsActive);

			return AppSetting.updateOrCreate<AppSetting>({
				service: 'theme',
				name: 'active-theme',
			}, {
				value: setDarkThemeAsActive ? 'dark' : 'light',
			});
		});

		dispatcher.listen('theme.*-mobile-sidebar', (event, payload) => {
			return AppSetting.updateOrCreate<AppSetting>({
				service: 'theme',
				name: 'mobile-sidebar-open',
			}, {
				value: event.name.endsWith(THEME_TOGGLE_MOBILE_SIDEBAR) ?
					!payload.api.getState().theme.sidebar.mobile.open :
					event.name.endsWith(THEME_OPEN_MOBILE_SIDEBAR)
			});
		});

		dispatcher.listen('theme.*-sidebar-size', (event, payload) => {
			const oldSize = payload.api.getState().theme.sidebar.desktop.size;

			return AppSetting.updateOrCreate<AppSetting>({
				service: 'theme',
				name: 'desktop-sidebar-size',
			}, {
				value: event.name.endsWith(THEME_TOGGLE_SIDEBAR_SIZE) ?
					(oldSize === 'small' ? 'big' : 'small') :
					(event.name.endsWith(THEME_SET_SMALL_SIDEBAR_SIZE) ? 'small' : 'big')
			});
		});
	}

	protected toggleDarkTheme(state: boolean): void {
		if (document && document.body) {
			document.body.classList[state ? 'add' : 'remove']('dark-theme');
		}
	}

	protected get service() {
		return this.getStoredService<ITheme>('theme');
	}
}
